import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './accounting.css';

const EditBank = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [bank, setBank] = useState(null);
  const [formData, setFormData] = useState({
    bank_name: '',
    swift_code: '',
    iban_prefix: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    website: '',
    logo: null
  });

  useEffect(() => {
    fetchBank();
  }, [id]);

  const fetchBank = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/accounting/banks/${id}`);
      const bankData = response.data;
      setBank(bankData);
      setFormData({
        bank_name: bankData.bank_name || '',
        swift_code: bankData.swift_code || '',
        iban_prefix: bankData.iban_prefix || '',
        contact_person: bankData.contact_person || '',
        phone: bankData.phone || '',
        email: bankData.email || '',
        address: bankData.address || '',
        website: bankData.website || '',
        logo: null
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ في تحميل بيانات البنك');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      await axios.put(`/api/accounting/banks/${id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      navigate('/banks');
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ في تحديث البنك');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bank-form-container">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !bank) {
    return (
      <div className="bank-form-container">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bank-form-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h2>{t('accounting.banks.editBank')}</h2>
          <p>تعديل بيانات البنك: {bank?.bank_code}</p>
        </div>
        <div className="header-actions">
          <button 
            className="back-btn"
            onClick={() => navigate('/banks')}
          >
            <i className="fas fa-arrow-right"></i>
            {t('accounting.banks.actions.back')}
          </button>
        </div>
      </div>

      <div className="form-container">
        {/* Help Panels */}
        <div className="help-panels">
          <div className="help-panel">
            <h4>{t('accounting.banks.help.title')}</h4>
            <p>{t('accounting.banks.help.description')}</p>
            <ul>
              <li>{t('accounting.banks.help.bankInfo')}</li>
              <li>{t('accounting.banks.help.accountInfo')}</li>
              <li>{t('accounting.banks.help.statusInfo')}</li>
            </ul>
          </div>
          <div className="guide-panel">
            <h4>ملاحظات التعديل</h4>
            <ul>
              <li>تم إنشاء هذا البنك في: {new Date(bank?.created_at).toLocaleDateString('ar-SA')}</li>
              <li>آخر تحديث: {new Date(bank?.updated_at).toLocaleDateString('ar-SA')}</li>
              <li>الحالة الحالية: {bank?.is_active ? 'نشط' : 'غير نشط'}</li>
            </ul>
          </div>
        </div>

        {/* Form Section */}
        <div className="form-section">
          <div className="form-card">
            <form onSubmit={handleSubmit} className="bank-form">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="form-label required">
                    {t('accounting.banks.bankName')}
                  </label>
                  <div className="form-note">
                    اسم البنك كما يظهر في الوثائق الرسمية
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleInputChange}
                    placeholder="مثال: البنك الأهلي السعودي"
                    required
                  />
                </div>

                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.swiftCode')}
                  </label>
                  <div className="form-note">
                    رمز السويفت الدولي للبنك (اختياري)
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    name="swift_code"
                    value={formData.swift_code}
                    onChange={handleInputChange}
                    placeholder="مثال: NCBKSAJE"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.ibanPrefix')}
                  </label>
                  <div className="form-note">
                    البادئة المستخدمة في أرقام الآيبان (اختياري)
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    name="iban_prefix"
                    value={formData.iban_prefix}
                    onChange={handleInputChange}
                    placeholder="مثال: SA"
                  />
                </div>

                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.contactPerson')}
                  </label>
                  <div className="form-note">
                    اسم الشخص المسؤول عن التواصل مع البنك
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleInputChange}
                    placeholder="اسم الشخص المسؤول"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.phone')}
                  </label>
                  <div className="form-note">
                    رقم الهاتف للتواصل مع البنك
                  </div>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+966 11 123 4567"
                  />
                </div>

                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.email')}
                  </label>
                  <div className="form-note">
                    البريد الإلكتروني للتواصل مع البنك
                  </div>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="info@bank.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-12">
                  <label className="form-label">
                    {t('accounting.banks.address')}
                  </label>
                  <div className="form-note">
                    العنوان الكامل للبنك
                  </div>
                  <textarea
                    className="form-control"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="العنوان الكامل للبنك"
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.website')}
                  </label>
                  <div className="form-note">
                    الموقع الإلكتروني للبنك
                  </div>
                  <input
                    type="url"
                    className="form-control"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://www.bank.com"
                  />
                </div>

                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.logo')}
                  </label>
                  <div className="form-note">
                    شعار البنك (اختياري) - اتركه فارغاً للاحتفاظ بالشعار الحالي
                  </div>
                  <input
                    type="file"
                    className="form-control"
                    name="logo"
                    onChange={handleInputChange}
                    accept="image/*"
                  />
                  {bank?.logo && (
                    <div className="mt-2">
                      <small className="text-muted">الشعار الحالي: {bank.logo}</small>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => navigate('/banks')}
                  disabled={saving}
                >
                  {t('accounting.banks.actions.back')}
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      جاري الحفظ...
                    </>
                  ) : (
                    t('accounting.banks.actions.save')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBank;
